import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ItemCreationSection } from '~/Components';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { isObjectEmpty } from '~/Helpers/CleanData.helper';
import { useNavigation, useNavigationContext, useProvider } from '~/Hooks';
import { AccessTypeModel } from '~/Models/DTO/AccessType.model';
import { IStaff } from '~/Models/DTO/Staff.model';
import { selectLoading } from '~/store/app';
import { ExtendedStaff } from './ExtendedSaff.model';
import { StaffEditSection } from './StaffEditSections';
import { staffVerification } from './verification';
import { Route } from '~/Constants/Routes.constants';
import { useRequestStaff, useRequestStaffCreate, useRequestStaffUpdate } from '~/requests';

const _initialValues: ExtendedStaff = {
  id: '',
  firstName: '',
  lastName: '',
  imageUrl: '',
  email: '',
  providerId: '',
  phone: '',
  birthDate: '',
  accessType: AccessTypeModel.NoAccess,
  disabled: false,
  descriptions: [{ language: 'EN', text: '' }],
};

export const useStaffEdit = () => {
  const { getSearchParams, dangerouslyNavigate } = useNavigation();
  const { id } = getSearchParams();
  const { currentProvider } = useProvider();
  const { blockNavigation } = useNavigationContext();
  const { data: staff, isLoading: isStaffLoading, error: staffError } = useRequestStaff(id);
  const {
    mutate: updateStaff,
    isSuccess: isStaffUpdateSuccess,
    error: staffUpdateError,
    isLoading: isStaffUpdateLoading,
  } = useRequestStaffUpdate();
  const {
    mutate: createStaff,
    isSuccess: isStaffCreateSuccess,
    error: staffCreateError,
    isLoading: isStaffCreateLoading,
  } = useRequestStaffCreate();

  //store
  const imageUploading = useSelector(selectLoading(REQUEST_ACTIONS.UPLOAD_IMAGE));
  // state
  const [currentStaff, setCurrentStaff] = useState<ExtendedStaff | IStaff>(_initialValues);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  const initialValues = useMemo(() => staff || _initialValues, [staff]);

  const fullName = useMemo(() => {
    let output = '';
    if (currentStaff?.firstName) {
      output = currentStaff.firstName.charAt(0).toUpperCase() + currentStaff.firstName.slice(1);
    }
    if (currentStaff?.lastName) {
      output = `${output} ${
        currentStaff.lastName.charAt(0).toUpperCase() + currentStaff.lastName.slice(1)
      }`;
    }
    return output;
  }, [currentStaff]);

  const onSave = useCallback(async () => {
    const payload = Object.assign({}, currentStaff) as ExtendedStaff;
    if (currentStaff) {
      if (id) {
        await updateStaff(payload as IStaff);
      } else {
        delete payload.id;
        payload.providerId = currentProvider?.uuid || '';
        await createStaff(payload as IStaff);
      }
      blockNavigation(false);
    }
  }, [currentStaff, id, currentProvider]);

  const validate = useCallback((validateStaff: ExtendedStaff) => {
    const errorsStaff = staffVerification(validateStaff);
    const shouldDisable = !isObjectEmpty(errorsStaff);
    setIsSubmitDisabled(shouldDisable);
    setCurrentStaff(validateStaff);
  }, []);

  //compute
  const sections: ItemCreationSection<ExtendedStaff>[] = useMemo(
    () => [({ data }) => StaffEditSection(data, !!initialValues?.accessType)],
    [initialValues?.accessType],
  );

  //lifecycle
  useEffect(() => {
    if (staff) {
      setCurrentStaff(staff);
      validate(staff);
    }
  }, [staff]);

  useEffect(() => {
    if (isStaffUpdateSuccess || isStaffCreateSuccess) {
      dangerouslyNavigate(Route.PROVIDER_STAFF);
    }
  }, [isStaffUpdateSuccess, isStaffCreateSuccess]);

  return {
    id,
    listItems: sections,
    onSave,
    initialValues,
    validate,
    fullName,
    error: staffError || staffUpdateError || staffCreateError || {},
    isLoading: imageUploading || isStaffLoading || isStaffUpdateLoading || isStaffCreateLoading,
    isSubmitDisabled:
      isSubmitDisabled ||
      hasValidationErrors ||
      imageUploading ||
      isStaffLoading ||
      isStaffUpdateLoading ||
      isStaffCreateLoading,
    onValidationErrors: setHasValidationErrors,
  };
};
